import { Immutable } from "immer";
import React, { type ReactNode } from "react";
import { t } from "ttag";

import { FormInput } from "../../../forms/FormInput";
import { IStringField } from "../../../models/formFields";
import { IFormUUID } from "../../../models/nominals";
import { IWriteReviewTemplateField } from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { reviewFormTextChange } from "../reducers";
import { getWriteReviewFormState } from "../selectors";

type IProps = Immutable<{
    formUUID: IFormUUID;
    name: string;
    field: IWriteReviewTemplateField;
}>;

export const WriteReviewFormTextField = (props: IProps): ReactNode => {
    const dispatch = useAppDispatch();
    const formState = useAppSelector((state) =>
        getWriteReviewFormState(state.reviews, props),
    );

    const value = formState?.valuesText[props.name] ?? "";
    const errors = formState.errors[props.name] ?? [];
    const showErrors = formState.showErrors;

    const onChange = (e: React.FormEvent<HTMLInputElement>) => {
        dispatch(
            reviewFormTextChange({
                formUUID: props.formUUID,
                fieldName: props.name,
                fieldValue: e.currentTarget.value,
            }),
        );
    };

    let postFieldDisclaimer: React.JSX.Element | null = null;
    if (props.name === "email_collection") {
        postFieldDisclaimer = (
            <div className="write-review-form__email-disclaimer">
                Your email will not be displayed on the website. We will only
                use this if we have questions about your review.
            </div>
        );
    }

    const placeholders: { [name: string]: string | undefined } = {
        headline: t`e.g., Quality and comfortable product`,
        name: t`e.g., Happy Sleeper`,
        location: t`e.g., Boston, MA`,
        email_collection: t`e.g., jdoe@example.com`,
    };
    const placeholderText = placeholders[props.name] || "";

    const autocompletes: { [name: string]: string | undefined } = {
        name: "nickname",
        location: "address-level2",
        email_collection: "email",
    };
    const autocompleteText = autocompletes[props.name] || "";

    let inputType: Exclude<IStringField<string>["type"], undefined>;
    switch (props.field.type) {
        case "email":
            inputType = "email";
            break;
        default:
            inputType = "text";
            break;
    }

    return (
        <span>
            <FormInput
                autoComplete={autocompleteText}
                name={props.name}
                id={props.name}
                type={inputType}
                required={props.field.is_required}
                label={props.field.name}
                labelPlacement={"static"}
                placeholder={placeholderText}
                maxLength={props.field.max_length || undefined}
                value={value}
                errors={errors}
                showErrorMessages={showErrors}
                onChange={onChange}
            />
            {postFieldDisclaimer}
        </span>
    );
};
